import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errorMsg, nombreModelo, modeloIdDe, pedidoDeRepuesto, ESTADO_PEDIDO_LABEL, fmtFechaCorta } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { numTrabajo } from "@/pages/TrabajosActivos";
import { EstadoSwitch } from "@/components/EstadoSwitch";
import { EliminarConConfirmacion } from "@/components/EliminarConConfirmacion";
import { FrtDialog } from "@/components/FrtDialog";
import { FrtLoteDialog } from "@/components/FrtLoteDialog";
import { EquivalentesDialog } from "@/components/EquivalentesDialog";
import { AvisoSinGrupoTarifa } from "@/components/AvisoSinGrupoTarifa";
import { ChevronDown, ChevronRight, CornerDownRight, Timer, AlertTriangle, ListChecks, Replace, Truck } from "lucide-react";

const fmtColones = (n) => `₡ ${Number(n || 0).toLocaleString("es-CR")}`;

const PRIORIDAD_LABEL = { alta: "Alta", media: "Media", baja: "Baja" };

export default function TrabajoAprobacion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [trabajo, setTrabajo] = useState(null);

  const cargar = async () => {
    const { data } = await api.get(`/trabajos/${id}`);
    setTrabajo(data);
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const manoObraServicio = (sol) => Number(
    sol.mano_obra_efectiva !== undefined && sol.mano_obra_efectiva !== null
      ? sol.mano_obra_efectiva : (sol.mano_obra || 0));

  const cobrarIgual = async (sol, valor) => {
    try {
      const { data } = await api.post(`/trabajos/${id}/solicitudes/${sol.id}/cobrar-igual`, { valor });
      setTrabajo(data);
      toast.success(valor ? "Se cobrará la mano de obra de todos modos" : "Mano de obra incluida nuevamente");
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const [frtOpen, setFrtOpen] = useState(false);
  const [frtPedido, setFrtPedido] = useState("");
  const [loteOpen, setLoteOpen] = useState(false);
  const [loteData, setLoteData] = useState(null);
  const [loteCargando, setLoteCargando] = useState(false);
  const [equivSol, setEquivSol] = useState(null);

  const abrirFrt = (pedido) => { setFrtPedido(pedido || ""); setFrtOpen(true); };

  const cubiertosPorFrt = useMemo(() => new Set(
    (trabajo?.solicitudes || [])
      .filter((s) => s.origen === "frt" && s.estado !== "rechazado")
      .map((s) => ((s.ref_id || "").split(":")[2] || "").trim())
      .filter(Boolean)), [trabajo]);

  // Repuestos cubiertos por una línea de conjunto "AMBOS" (ver agregarDesdeEstimador): se
  // guardan por ref_id en cubre_ref_ids, no por nombre, porque cubren 2 repuestos a la vez.
  const cubiertosPorConjunto = useMemo(() => new Set(
    (trabajo?.solicitudes || [])
      .filter((s) => s.estado !== "rechazado" && Array.isArray(s.cubre_ref_ids))
      .flatMap((s) => s.cubre_ref_ids)
      .filter(Boolean)), [trabajo]);

  // ref_id del repuesto -> nombre del servicio sugerido que lo cubre (ver agregarDesdeEstimador).
  const servicioSugeridoPorRef = useMemo(() => {
    const m = {};
    (trabajo?.solicitudes || [])
      .filter((s) => s.tipo === "servicio" && (s.origen || "").startsWith("estimado:") && s.estado !== "rechazado")
      .forEach((s) => {
        const refId = s.origen.slice("estimado:".length);
        if (!m[refId]) m[refId] = s.nombre;
      });
    return m;
  }, [trabajo]);

  const pendientesSinMO = () => {
    const sols = trabajo?.solicitudes || [];
    const serviciosPorRef = {};
    sols.forEach((s) => { if (s.tipo === "servicio") serviciosPorRef[s.ref_id] = s; });
    const tieneRepuestosDePlantilla = (sid) => sols.some((x) => x.origen === `plantilla:${sid}`);
    const filtradas = sols.filter((s) => {
      if (s.estado === "rechazado" || s.origen === "frt" || cubiertosPorFrt.has(s.nombre) || cubiertosPorConjunto.has(s.ref_id)
        || (s.tipo === "repuesto" && servicioSugeridoPorRef[s.ref_id])) return false;
      const m = (s.origen || "").match(/^plantilla:(.+)$/);
      if (m) {
        // Repuesto de un servicio SIN plantilla o con MO efectiva 0 y SIN "incluida_en": si el
        // padre está incluido en otro servicio, su mano de obra ya es de ese otro (no es 0 real).
        const padre = serviciosPorRef[m[1]];
        return !!padre && !padre.incluida_en && (!padre.plantilla_id || manoObraServicio(padre) === 0);
      }
      if (Number(s.mano_obra || 0)) return false;
      // Servicio sin plantilla/sin MO cuyos propios repuestos YA se están estimando (arriba):
      // no se manda también su línea genérica (su nombre no es una operación FRT real).
      if (s.tipo === "servicio" && (!s.plantilla_id || manoObraServicio(s) === 0) && tieneRepuestosDePlantilla(s.ref_id)) {
        return false;
      }
      return true;
    });
    // Deduplicar por ref_id: el mismo repuesto puede aparecer bajo varios servicios padre.
    const vistos = new Set();
    return filtradas.filter((s) => {
      if (vistos.has(s.ref_id)) return false;
      vistos.add(s.ref_id);
      return true;
    });
  };

  const estimarLote = async () => {
    const items = pendientesSinMO().map((s) => ({
      nombre: s.nombre, codigo: s.codigo || "", repuesto_id: s.tipo === "repuesto" ? s.ref_id : null }));
    if (!items.length) { toast.info("No hay ítems marcados 'Sin mano de obra'"); return; }
    setLoteCargando(true);
    try {
      const { data } = await api.post("/frt/estimar-lote",
        { modelo_id: modeloIdDe(trabajo), items, trabajo_id: id }, { timeout: 180000 });
      if (data.sin_grupo) { toast.warning(data.mensaje); return; }
      setLoteData(data);
      setLoteOpen(true);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setLoteCargando(false); }
  };

  const mapActual = ({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit, cobrar_igual, cubre_ref_ids }) =>
    ({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit, cobrar_igual, cubre_ref_ids });

  // "Estimar mano de obra de los pendientes": un servicio sugerido + líneas FRT en UN SOLO PUT
  // (evita el closure viejo de dos PUT separados, que perdía el servicio recién agregado).
  const agregarDesdeEstimador = async ({ servicios = [], frt = [], conjuntos = [] }) => {
    const actuales = trabajo?.solicitudes || [];
    const nuevosServicios = (servicios || [])
      .map((sg) => ({ id: null, tipo: "servicio", ref_id: sg.servicio_id, codigo: "",
        nombre: sg.servicio_nombre, categoria: "", cantidad: 1, prioridad: "media",
        origen: sg.repuesto_ref_id ? `estimado:${sg.repuesto_ref_id}` : "manual",
        plantilla_id: sg.plantilla_id, mano_obra: sg.mano_obra }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    const nuevosFrt = (frt || [])
      .map((c) => ({ id: null, tipo: "servicio",
        ref_id: `frt:${c.id}${c.repuesto ? `:${c.repuesto}` : ""}`, codigo: "FRT",
        nombre: `${c.operacion}${c.alcance ? ` (${c.alcance})` : ""} — ESTIMADO FRT ${c.frt_horas}h${c.repuesto ? ` · MO de: ${c.repuesto}` : ""}`,
        categoria: "", cantidad: 1, prioridad: "media", origen: "frt",
        plantilla_id: null, mano_obra: c.total }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    const nuevosConjuntos = (conjuntos || [])
      .map((g) => ({ id: null, tipo: "servicio",
        ref_id: `frt:${g.id}:${g.repuesto1.nombre}+${g.repuesto2.nombre}`, codigo: "FRT",
        nombre: `${g.operacion} ${g.alcance} — ESTIMADO FRT ${g.frt_horas}h · MO de: ${g.repuesto1.nombre} + ${g.repuesto2.nombre}`,
        categoria: "", cantidad: 1, prioridad: "media",
        origen: g.repuesto1.ref_id ? `estimado:${g.repuesto1.ref_id}` : "frt",
        cubre_ref_ids: [g.repuesto1.ref_id, g.repuesto2.ref_id].filter(Boolean),
        plantilla_id: null, mano_obra: g.total }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    const nuevos = [...nuevosServicios, ...nuevosFrt, ...nuevosConjuntos];
    if (!nuevos.length) return;
    try {
      const { data } = await api.put(`/trabajos/${id}/solicitudes`, {
        solicitudes: [...actuales.map(mapActual), ...nuevos],
      });
      setTrabajo(data);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const agregarEstimadosFrt = async (items) => {
    const actuales = trabajo?.solicitudes || [];
    const nuevos = items
      .map((c) => ({ id: null, tipo: "servicio",
        ref_id: `frt:${c.id}${c.repuesto ? `:${c.repuesto}` : ""}`, codigo: "FRT",
        nombre: `${c.operacion}${c.alcance ? ` (${c.alcance})` : ""} — ESTIMADO FRT ${c.frt_horas}h${c.repuesto ? ` · MO de: ${c.repuesto}` : ""}`,
        categoria: "", cantidad: 1, prioridad: "media", origen: "frt",
        plantilla_id: null, mano_obra: c.total }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    if (!nuevos.length) return;
    try {
      const { data } = await api.put(`/trabajos/${id}/solicitudes`, {
        solicitudes: [...actuales.map(mapActual), ...nuevos],
      });
      setTrabajo(data);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  // Interruptor Pendiente/Aprobado: un cambio en el servicio (padre) se propaga a todos sus
  // repuestos heredados en una sola llamada a /estado-lote; un cambio en un repuesto hijo
  // recalcula el estado del servicio solo si TODOS sus hermanos quedan aprobados o TODOS
  // quedan sin aprobar, y también usa /estado-lote cuando toca a ambos (hijo + servicio).
  const cambiarEstadoFila = async (sol, rol) => {
    const nuevo = sol.estado === "aprobado" ? "pendiente" : "aprobado";
    try {
      if (rol === "padre") {
        const hijos = solicitudes.filter((x) => x.origen === `plantilla:${sol.ref_id}`);
        const ids = [sol.id, ...hijos.map((h) => h.id)];
        const { data } = ids.length > 1
          ? await api.post(`/trabajos/${id}/solicitudes/estado-lote`, { ids, estado: nuevo })
          : await api.post(`/trabajos/${id}/solicitudes/${sol.id}/estado`, { estado: nuevo });
        setTrabajo(data);
        return;
      }
      const m = (sol.origen || "").match(/^plantilla:(.+)$/);
      if (rol === "hijo" && m) {
        const hermanos = solicitudes.filter((x) => x.origen === sol.origen);
        const estadosFinales = hermanos.map((x) => (x.id === sol.id ? nuevo : x.estado));
        const todosOn = estadosFinales.every((e) => e === "aprobado");
        const todosOff = estadosFinales.every((e) => e !== "aprobado");
        const padre = solicitudes.find((x) => x.tipo === "servicio" && x.ref_id === m[1]);
        const ids = [sol.id];
        if (padre && (todosOn || todosOff) && padre.estado !== nuevo) ids.push(padre.id);
        const { data } = ids.length > 1
          ? await api.post(`/trabajos/${id}/solicitudes/estado-lote`, { ids, estado: nuevo })
          : await api.post(`/trabajos/${id}/solicitudes/${sol.id}/estado`, { estado: nuevo });
        setTrabajo(data);
        return;
      }
      const { data } = await api.post(`/trabajos/${id}/solicitudes/${sol.id}/estado`, { estado: nuevo });
      setTrabajo(data);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  // X = borrar la línea (y, si es servicio, sus repuestos heredados) de la boleta.
  // eliminar_ids fuerza el borrado aunque la línea esté aprobada/rechazada (ver guardar_solicitudes).
  const eliminarSolicitud = async (sol) => {
    const actuales = trabajo?.solicitudes || [];
    let restantes = actuales.filter((s) => s.id !== sol.id);
    const eliminarIds = [sol.id];
    if (sol.tipo === "servicio") {
      const hijos = actuales.filter((s) => s.origen === `plantilla:${sol.ref_id}`);
      restantes = restantes.filter((s) => s.origen !== `plantilla:${sol.ref_id}`);
      eliminarIds.push(...hijos.map((h) => h.id));
    }
    try {
      const { data } = await api.put(`/trabajos/${id}/solicitudes`, {
        solicitudes: restantes.map(mapActual), eliminar_ids: eliminarIds,
      });
      setTrabajo(data);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const solicitudes = useMemo(() => trabajo?.solicitudes || [], [trabajo]);
  const [colapsados, setColapsados] = useState({});

  const filasAgrupadas = useMemo(() => {
    const presentes = new Set(solicitudes.filter((s) => s.tipo === "servicio").map((s) => s.ref_id));
    const hijosDe = {};
    solicitudes.forEach((s, i) => {
      const m = (s.origen || "").match(/^plantilla:(.+)$/);
      if (m && presentes.has(m[1])) (hijosDe[m[1]] = hijosDe[m[1]] || []).push({ s, i });
    });
    const enGrupo = new Set(Object.values(hijosDe).flat().map((x) => x.i));
    const frt = [];
    solicitudes.forEach((s, i) => { if (s.origen === "frt" || (s.origen || "").startsWith("estimado:")) frt.push({ s, i }); });
    const enFrt = new Set(frt.map((x) => x.i));
    const filas = [];
    solicitudes.forEach((s, i) => {
      if (enGrupo.has(i) || enFrt.has(i)) return;
      const hijos = s.tipo === "servicio" ? hijosDe[s.ref_id] || [] : [];
      if (hijos.length) {
        filas.push({ s, i, rol: "padre", nHijos: hijos.length });
        if (!colapsados[s.ref_id]) hijos.forEach((h) => filas.push({ ...h, rol: "hijo", padreNombre: s.nombre }));
      } else {
        filas.push({ s, i, rol: "suelto" });
      }
    });
    if (frt.length) {
      const total = frt.reduce((a, x) =>
        a + (x.s.estado !== "rechazado" ? Number(x.s.mano_obra || 0) : 0), 0);
      filas.push({ rol: "frt-padre", i: "frt-padre", nHijos: frt.length, total });
      if (!colapsados.__frt__) frt.forEach((x) => filas.push({ ...x, rol: "frt-hijo" }));
    }
    return filas;
  }, [solicitudes, colapsados]);

  // Agrupa las filas renderizadas (padre+hijos o frt-padre+frt-hijos, y sueltas) para que cada
  // grupo viva en su propio <tbody>, con una fila separadora de 12px entre grupos.
  const gruposRender = useMemo(() => {
    const grupos = [];
    let actual = null;
    filasAgrupadas.forEach((f) => {
      if (f.rol === "padre" || f.rol === "frt-padre") {
        actual = { filas: [f] };
        grupos.push(actual);
      } else if ((f.rol === "hijo" || f.rol === "frt-hijo") && actual) {
        actual.filas.push(f);
      } else {
        grupos.push({ filas: [f] });
        actual = null;
      }
    });
    grupos.forEach((g) => {
      const ultimo = g.filas[g.filas.length - 1];
      if (ultimo.rol === "hijo" || ultimo.rol === "frt-hijo") {
        g.filas[g.filas.length - 1] = { ...ultimo, esUltimoHijo: true };
      }
    });
    return grupos;
  }, [filasAgrupadas]);

  const parseFrt = (s) => {
    const m = (s.nombre || "").match(/^(.*?) — ESTIMADO FRT ([\d.]+)h(?: · MO de: (.*))?$/);
    return m ? { operacion: m[1], horas: m[2], repuesto: m[3] || "" }
      : { operacion: s.nombre, horas: "", repuesto: "" };
  };

  const totalMO = (filtro) => solicitudes
    .filter((s) => s.tipo === "servicio" && (!filtro || s.estado === filtro))
    .reduce((acc, s) => acc + manoObraServicio(s), 0);

  return (
    <div data-testid="trabajo-aprobacion-page" className="space-y-4">
      <Panel titulo={`Aprobación de solicitudes — Trabajo ${trabajo ? numTrabajo(trabajo.numero) : ""}`}
        testId="aprobacion-panel">
        {trabajo && (
          <p className="text-sm text-slate-600 mb-4" data-testid="aprobacion-info">
            {trabajo.vehiculo?.placa} · {nombreModelo(trabajo.vehiculo?.marca, trabajo.vehiculo?.modelo)} · {trabajo.cliente?.nombre}
          </p>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" data-testid="aprobacion-frt-lote-btn"
            disabled={!modeloIdDe(trabajo) || loteCargando || !tienePermiso("trabajos.estimar_mo")} onClick={estimarLote}
            className="border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800">
            <ListChecks size={14} className="mr-1.5" />
            {loteCargando ? "Estimando pendientes..." : "Estimar mano de obra de los pendientes"}
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid="aprobacion-frt-btn"
            disabled={!modeloIdDe(trabajo) || !tienePermiso("trabajos.estimar_mo")} onClick={() => abrirFrt("")}
            className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800">
            <Timer size={14} className="mr-1.5" /> Estimar mano de obra por FRT
            <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">sin plantilla</span>
          </Button>
          <span className="text-xs text-slate-400">Plantilla primero; si no hay, se estima con horas FRT × tarifa del grupo del modelo</span>
        </div>
        {trabajo?.sin_grupo_tarifa && <div className="mb-4"><AvisoSinGrupoTarifa testId="aprobacion-aviso-sin-grupo-tarifa" /></div>}
        <div className="overflow-x-auto">
          <table data-testid="aprobacion-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 pl-4 font-semibold">Estado</th>
                <th className="py-2.5 pr-3 font-semibold">Repuesto / Servicio</th>
                <th className="py-2.5 pr-3 font-semibold">Cant.</th>
                <th className="py-2.5 pr-3 font-semibold">Prioridad</th>
                <th className="py-2.5 pr-3 font-semibold text-right">Mano de obra</th>
                <th className="py-2.5 pr-4 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            {gruposRender.map((g, gi) => (
              <tbody key={gi}>
                {gi > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={6} style={{ height: 12, padding: 0, border: "none", background: "transparent" }} />
                  </tr>
                )}
                {g.filas.map(({ s, i, rol, nHijos, total, esUltimoHijo }) => {
                if (rol === "frt-padre") {
                  return (
                    <tr key="frt-padre" data-testid="aprobacion-frt-padre" className="bg-[#B45309] text-white">
                      <td className="py-2.5 pr-3 pl-4 text-xs text-white/70">—</td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <button type="button" data-testid="aprobacion-frt-colapsar"
                            title={colapsados.__frt__ ? "Mostrar estimaciones" : "Ocultar estimaciones"}
                            onClick={() => setColapsados((c) => ({ ...c, __frt__: !c.__frt__ }))}
                            className="w-5 h-5 flex items-center justify-center rounded text-white hover:bg-white/15">
                            {colapsados.__frt__ ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                          </button>
                          <span className="font-medium text-[15px] text-white">Mano de obra estimada</span>
                          <span data-testid="aprobacion-frt-count"
                            className="chip rounded-full bg-white text-[#B45309]">
                            {nHijos} {nHijos === 1 ? "estimación" : "estimaciones"}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 text-white/90">—</td>
                      <td className="py-2.5 pr-3 text-white/90">—</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-white"
                        data-testid="aprobacion-frt-total">
                        {fmtColones(total)}
                      </td>
                      <td className="py-2.5 pr-4"></td>
                    </tr>
                  );
                }
                const frtInfo = rol === "frt-hijo" && s.origen === "frt" ? parseFrt(s) : null;
                const claseFila = rol === "padre" ? "bg-[#216295] text-white"
                  : rol === "hijo" || rol === "frt-hijo" ? `bg-white border-x border-slate-300 ${esUltimoHijo ? "border-b border-slate-300" : "border-b border-slate-200"}`
                  : "border-b border-slate-100";
                const codigoCat = [s.codigo, s.categoria].filter(Boolean).join(" · ");
                const pedidoEsp = pedidoDeRepuesto(trabajo, s);
                return (
                  <tr key={s.id} data-testid={`aprobacion-row-${i}`} data-rol={rol} className={claseFila}>
                    <td className="py-2.5 pr-3 pl-4">
                      <EstadoSwitch estado={s.estado} onDark={rol === "padre"}
                        disabled={!tienePermiso("trabajos.aprobar")}
                        onChange={() => cambiarEstadoFila(s, rol)}
                        testId={`aprobacion-estado-${i}`} />
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className={rol === "hijo" || rol === "frt-hijo" ? "ml-3 sm:ml-6" : ""}>
                        {rol === "frt-hijo" ? (
                          s.origen === "frt" ? (
                          <>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-slate-800">{frtInfo.operacion}</span>
                              {frtInfo.horas && (
                                <span className="text-xs text-slate-500">{frtInfo.horas}h</span>
                              )}
                              <span className="chip rounded bg-amber-100 uppercase text-amber-700">
                                Estimado por FRT
                              </span>
                            </div>
                            {frtInfo.repuesto && (
                              <div className="mt-0.5 text-xs">
                                <span data-testid={`aprobacion-frt-de-${i}`}
                                  className="chip gap-1 rounded bg-amber-100 text-amber-800">
                                  <CornerDownRight size={11} /> de: {frtInfo.repuesto}
                                </span>
                              </div>
                            )}
                          </>
                          ) : (
                          <>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-slate-800">{s.nombre}</span>
                              <span className="chip rounded bg-amber-100 uppercase text-amber-700">
                                Servicio sugerido
                              </span>
                            </div>
                            <div className="mt-0.5 text-xs">
                              <span data-testid={`aprobacion-sugerido-de-${i}`}
                                className="chip gap-1 rounded bg-amber-100 text-amber-800">
                                <CornerDownRight size={11} /> de: {solicitudes.find((x) => x.ref_id === s.origen.slice("estimado:".length))?.nombre || "repuesto"}
                              </span>
                            </div>
                          </>
                          )
                        ) : (
                        <>
                        <div className="flex items-center gap-2">
                          {rol === "padre" && (
                            <button type="button" data-testid={`aprobacion-colapsar-${i}`}
                              title={colapsados[s.ref_id] ? "Mostrar repuestos heredados" : "Ocultar repuestos heredados"}
                              onClick={() => setColapsados((c) => ({ ...c, [s.ref_id]: !c[s.ref_id] }))}
                              className="w-5 h-5 flex items-center justify-center rounded text-white hover:bg-white/15">
                              {colapsados[s.ref_id] ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                            </button>
                          )}
                          <span className={`font-medium ${rol === "padre" ? "text-[14px] text-white" : "text-slate-800"}`}>{s.nombre}</span>
                          {s.tipo === "repuesto" && s.opcional && (
                            <span data-testid={`aprobacion-opcional-badge-${i}`}
                              className="chip shrink-0 rounded-full bg-sky-100 text-sky-700">
                              Opcional
                            </span>
                          )}
                          {rol === "padre" && (
                            <span data-testid={`aprobacion-hijos-count-${i}`}
                              className="chip rounded-full bg-white text-[#216295]">
                              {nHijos} repuesto{nHijos > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${rol === "padre" ? "ml-7 text-white/80" : "text-slate-500"}`}>
                          {codigoCat && <span className="text-[12px]">{codigoCat}</span>}
                          {(s.origen === "ingreso" || s.origen?.startsWith("ingreso:")) && <span className={rol === "padre" ? "text-white/80" : "text-teal-600"}>del ingreso del trabajo</span>}
                          {s.tipo === "repuesto" && modeloIdDe(trabajo) && (
                            <button type="button" data-testid={`aprobacion-equivalentes-${i}`}
                              title="Ver repuestos equivalentes de modelos compatibles"
                              onClick={() => setEquivSol(s)}
                              className="chip gap-1 rounded-full border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                              <Replace size={11} /> Equivalentes
                            </button>
                          )}
                          {s.tipo === "repuesto" && pedidoEsp && (
                            <button type="button" data-testid={`aprobacion-pedido-especial-chip-${i}`}
                              onClick={() => navigate(`/pedidos-especiales/${pedidoEsp.id}`)}
                              className={`chip gap-1 rounded-full border ${pedidoEsp.atrasado
                                ? "border-pink-300 bg-pink-50 text-pink-700" : "border-violet-300 bg-violet-50 text-violet-700"}`}>
                              <Truck size={11} /> {pedidoEsp.numero_fmt} · {ESTADO_PEDIDO_LABEL[pedidoEsp.estado]}
                              {pedidoEsp.fecha_estimada_arribo ? ` · llega ${fmtFechaCorta(pedidoEsp.fecha_estimada_arribo)}` : ""}
                            </button>
                          )}
                        </div>
                        </>
                        )}
                      </div>
                    </td>
                    <td className={`py-2.5 pr-3 ${rol === "padre" ? "text-white" : "text-slate-700"}`}>{s.tipo === "repuesto" ? s.cantidad : "—"}</td>
                    <td className={`py-2.5 pr-3 ${rol === "padre" ? "text-white" : "text-slate-600"}`}>{PRIORIDAD_LABEL[s.prioridad] || s.prioridad}</td>
                    <td className="py-2.5 pr-3 text-right" data-testid={`aprobacion-mo-${i}`}>
                      {s.tipo === "servicio" && s.cubierta_por ? (
                        s.cobrar_igual ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <span className={rol === "padre" ? "text-white" : "text-slate-700"}>{fmtColones(manoObraServicio(s))}</span>
                            {tienePermiso("trabajos.estimar_mo") && (
                            <button type="button" data-testid={`aprobacion-reincluir-${i}`}
                              onClick={() => cobrarIgual(s, false)}
                              className="text-[11px] font-medium text-slate-500 underline hover:text-teal-700">
                              Volver a incluir
                            </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5">
                            <span data-testid={`aprobacion-incluida-${i}`}
                              className="chip rounded bg-teal-100 text-teal-800">
                              Incluida en {s.cubierta_por}
                            </span>
                            {tienePermiso("trabajos.estimar_mo") && (
                            <button type="button" data-testid={`aprobacion-cobrar-igual-${i}`}
                              onClick={() => cobrarIgual(s, true)}
                              className="text-[11px] font-medium text-amber-600 underline hover:text-amber-700">
                              Cobrar de todos modos
                            </button>
                            )}
                          </div>
                        )
                      ) : s.tipo === "servicio" && manoObraServicio(s) > 0 ? (
                        <span className={rol === "padre" ? "text-white" : "text-slate-700"}>{fmtColones(manoObraServicio(s))}</span>
                      ) : (cubiertosPorFrt.has(s.nombre) || cubiertosPorConjunto.has(s.ref_id)) ? (
                        <span data-testid={`aprobacion-mo-cubierta-${i}`}
                          className="text-xs font-medium text-teal-700">estimada por FRT ↓</span>
                      ) : s.tipo === "repuesto" && servicioSugeridoPorRef[s.ref_id] ? (
                        <span data-testid={`aprobacion-mo-en-servicio-${i}`}
                          className="text-xs font-medium text-teal-700">en: {servicioSugeridoPorRef[s.ref_id]} ↓</span>
                      ) : (s.tipo === "servicio" || rol === "suelto") && tienePermiso("trabajos.estimar_mo") ? (
                        <button type="button" data-testid={`aprobacion-sin-mo-${i}`}
                          title="Sin mano de obra asignada — clic para estimar por FRT"
                          onClick={() => abrirFrt(s.nombre)}
                          className="chip gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100">
                          <AlertTriangle size={11} /> Sin mano de obra
                        </button>
                      ) : (
                        <span className={rol === "padre" ? "text-white" : "text-slate-700"}>—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex justify-end">
                        {tienePermiso("trabajos.borrar_lineas") && (
                        <EliminarConConfirmacion
                          mensaje={s.tipo === "servicio" ? "¿Eliminar el servicio y sus repuestos? No aparecerán en la boleta." : "¿Eliminar esta línea? No aparecerá en la boleta."}
                          onConfirmar={() => eliminarSolicitud(s)}
                          testId={`aprobacion-eliminar-${i}`} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            ))}
            {solicitudes.length === 0 && (
              <tbody>
                <tr><td colSpan={6} className="py-8 text-center text-slate-400" data-testid="aprobacion-vacio">
                  Este trabajo no tiene solicitudes registradas
                </td></tr>
              </tbody>
            )}
          </table>
        </div>
        {solicitudes.some((s) => s.tipo === "servicio") && (
          <div className="mt-4 flex flex-col items-end gap-1 text-sm" data-testid="aprobacion-totales">
            <div className="text-slate-600">
              Mano de obra solicitada: <b data-testid="aprobacion-total-solicitado">{fmtColones(totalMO())}</b>
            </div>
            <div className="text-teal-700">
              Mano de obra aprobada: <b data-testid="aprobacion-total-aprobado">{fmtColones(totalMO("aprobado"))}</b>
            </div>
          </div>
        )}
      </Panel>

      {modeloIdDe(trabajo) && (
        <FrtDialog open={frtOpen} onOpenChange={setFrtOpen}
          modeloId={modeloIdDe(trabajo)}
          modeloNombre={nombreModelo(trabajo.vehiculo.marca, trabajo.vehiculo.modelo)}
          pedidoInicial={frtPedido}
          onAgregar={agregarEstimadosFrt} />
      )}

      <FrtLoteDialog open={loteOpen} onOpenChange={setLoteOpen}
        data={loteData} onAgregar={agregarDesdeEstimador} />

      <EquivalentesDialog open={!!equivSol} trabajoId={id} solicitud={equivSol}
        onClose={() => setEquivSol(null)} onSustituido={(t) => setTrabajo(t)} />
    </div>
  );
}
